import React from 'react';
import PropTypes from 'prop-types';
import { URLS } from 'consts';
import themed from 'components/hoc/themed';
import {
  Compensation,
  getExperienceLevelDisplayValue,
  Industry,
  ProjectSummaryFlexFactors,
  projectApplicants,
} from './supporting';
import {
  Description,
  Label,
  ProjectSummaryContainer,
  ApplicantCount,
  LowAppCount,
} from './ProjectSummaryStyles';
import Briefcase from 'components/ui/icons/Briefcase';
import Clock from 'components/ui/icons/Clock';
import Badge from 'components/ui/Badge';
import LocationPin from 'components/ui/icons/LocationPin';
import Profile from 'components/ui/icons/Profile';
import ProjectExpiration from './ProjectExpiration';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import { Layout } from 'components/containers/shared/Layout';
import { getTimeFromNow, getFormattedDate } from 'utilities';

// We don't want draft projects to be edited from the /review screen for job_post_v2 because all fields
// are required on that screen. We want to allow them to save the draft without all fields - so we send
// them to the /edit step wizard instead.

const jobAction = project => {
  const archivedStatuses = [
    'filled',
    'cancelled',
    'pending_removal',
    'placed',
    'onboarding',
  ];
  if (archivedStatuses.includes(project.status)) {
    return {
      label: 'Reuse job',
      url: `${URLS.employerProjects}/${project.slug}/duplicate`,
    };
  } else {
    return {
      label: 'Edit job',
      url:
        project.status !== 'draft'
          ? URLS.employerProjectReview(project.slug)
          : `${URLS.employerProjects}/${project.slug}/edit`,
    };
  }
};
const DATE_FORMAT_OPTIONS = {
  month: 'short',
  year: 'numeric',
};
const ProjectSummary = ({
  expirationLabel,
  project,
  showEditLink,
  talentDisplay,
}) => {
  let badgeText;
  if (project.activated_recently) {
    badgeText = 'New';
  } else if (project.updated_recently) {
    badgeText = 'Updated';
  }
  return (
    <Layout title={project.project_title}>
      <div className="align-items-center d-flex flex-row mt-3">
        <Breadcrumbs
          history={[
            { name: 'Job posts', path: URLS.employerProjects },
            { name: project.project_title, path: '' },
          ]}
        />
      </div>
      {showEditLink && (
        <div className="d-flex pt-3 pb-3 justify-content-end">
          <a href={jobAction(project).url}>
            {jobAction(project).label}
            &nbsp;
            <i className="fa fa-pencil" />
          </a>
        </div>
      )}
      <ProjectSummaryContainer talentDisplay={talentDisplay}>
        <div className="project-summary__main-content">
          <h2>{project.project_title}</h2>
          <div className="d-flex mt-2 align-items-center">
            {badgeText && <Badge className="mr-2">{badgeText}</Badge>}
            {talentDisplay && !!expirationLabel && (
              <ProjectExpiration expirationLabel={expirationLabel} />
            )}
          </div>
          <div className="project-summary__location-industry project-summary__grid col-12 pl-0 pr-0">
            <div>
              <div className="mb-2 location-wrapper">
                <LocationPin width={20} height={20} />
                <div>
                  {project.location_remote_or_city_state_list.map(location => (
                    <span key={location}>{location}</span>
                  ))}
                </div>
              </div>
              {talentDisplay && project.employer_industry && (
                <>
                  <div className="mt-2">
                    <Briefcase width={20} height={20} />
                    {project.employer_industry}
                  </div>
                  <div className="mt-2">
                    <Compensation project={project} />
                  </div>
                </>
              )}
            </div>
            <div className="pt-sm-0 pt-3">
              {project.activated_at && (
                <>
                  <Clock
                    width={20}
                    height={20}
                    fillColor="currentColor"
                    className="mr-2 align-top"
                  />
                  {`Posted ${getTimeFromNow(project.activated_at)}`}
                </>
              )}
              {talentDisplay && (
                <div className="applicant-count d-flex flex-lg-nowrap">
                  <div>
                    <Profile width={18} height={18} className="align-top" />
                  </div>
                  <div className="d-flex flex-sm-nowrap flex-wrap">
                    <ApplicantCount>
                      {projectApplicants(project.applicant_count)}
                    </ApplicantCount>
                    {project.applicant_count <= 20 && talentDisplay && (
                      <LowAppCount>Be one of the first to apply!</LowAppCount>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          {!talentDisplay && (
            <>
              <div className="mb-4 d-flex flex-column flex-sm-row justify-content-between">
                <div className="mb-sm-0 mb-4">
                  <Label>Superpower</Label>
                  {project.superpower}
                </div>
                <Industry
                  industry={project.industry}
                  talentDisplay={talentDisplay}
                />
              </div>
              <div className="mb-4">
                <Label>Experience level</Label>
                {getExperienceLevelDisplayValue(
                  project.preferred_experience_level,
                )}
              </div>
            </>
          )}
          <div className="mb-4 project-summary__grid">
            <div>
              <Label>{talentDisplay ? 'Required ' : ''}Skills</Label>
              <ul>
                {project.capabilities.map(capability => (
                  <li className="project-skill-item" key={capability.slug}>
                    {capability.title}
                  </li>
                ))}
                {project.expertises &&
                  project.expertises.map(skill => (
                    <li className="project-skill-item" key={skill.id}>
                      {skill.label}
                    </li>
                  ))}
              </ul>
            </div>
            {talentDisplay && (
              <Industry
                industry={project.industry}
                talentDisplay={talentDisplay}
              />
            )}
          </div>
          {talentDisplay && (
            <ProjectSummaryFlexFactors
              project={project}
              talentDisplay={talentDisplay}
            />
          )}
        </div>
        {!talentDisplay && (
          <div className="align-left">
            <Compensation project={project} />
            <div className="mb-3 pt-4">
              <Label>Start date</Label>
              {project.start_date
                ? getFormattedDate(
                    new Date(project.start_date + 'T00:00:00'),
                    DATE_FORMAT_OPTIONS,
                  )
                : 'N/A'}
            </div>
            {!project.full_time && project.end_date && (
              <div className="mb-4">
                <Label>End date</Label>
                {getFormattedDate(
                  new Date(project.end_date + 'T00:00:00'),
                  DATE_FORMAT_OPTIONS,
                ) || 'N/A'}
              </div>
            )}
            <ProjectSummaryFlexFactors
              project={project}
              talentDisplay={talentDisplay}
            />
          </div>
        )}
      </ProjectSummaryContainer>
      <div className="project-summary__job-description mt-4 mt-md-0">
        <Label>Job description</Label>
        <Description
          dangerouslySetInnerHTML={{ __html: project.sanitized_description }}
        />
      </div>
    </Layout>
  );
};

ProjectSummary.propTypes = {
  expirationLabel: PropTypes.string,
  from: PropTypes.string,
  project: PropTypes.object.isRequired,
  showEditLink: PropTypes.bool,
  singleColumn: PropTypes.bool,
  talentDisplay: PropTypes.bool,
};

ProjectSummary.defaultProps = {
  showEditLink: false,
  singleColumn: false,
  talentDisplay: false,
};

export default themed(ProjectSummary);
